import { SchrankLocationNav } from '../components/schrank/SchrankLocationNav';
import { SchrankSectionHeader } from '../components/schrank/SchrankSectionHeader';
import { SchrankFloorPreview } from '../components/schrank/SchrankFloorPreview';
// Schrank-Management — Vollständige Integration (Phasen 1-4)
// Modi: 'overview' (Gebäude/Etagen-Liste) → 'floor' (FloorPlanEditor)
//       → 'room' (LockerWallEditor)
import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { schrankApi } from '../lib/api';
import MainLayout from '../components/layout/MainLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { FloorPlanEditor } from '../components/schrank/FloorPlanEditor';
import { LockerWallEditor } from '../components/schrank/LockerWallEditor';

// =============================================================================
// Building Dialog (Create/Edit)
// =============================================================================
function BuildingDialog({ open, onOpenChange, building, onSave }) {
  const [name, setName] = useState('');
  const [beschreibung, setBeschreibung] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(building?.name || '');
      setBeschreibung(building?.beschreibung || '');
    }
  }, [open, building]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Name ist erforderlich');
      return;
    }
    setIsSaving(true);
    try {
      await onSave({ name: name.trim(), beschreibung: beschreibung.trim() || null });
      onOpenChange(false);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="schrank-dialog">
        <DialogHeader>
          <DialogTitle>{building ? 'Gebäude bearbeiten' : 'Neues Gebäude'}</DialogTitle>
          <DialogDescription>
            Gebäude gruppieren mehrere Etagen für die Schrankverwaltung.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="b-name">Name *</Label>
            <Input id="b-name" value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="z.B. Hauptgebäude" data-testid="schrank-building-name" />
          </div>
          <div>
            <Label htmlFor="b-beschr">Beschreibung</Label>
            <Input id="b-beschr" value={beschreibung} onChange={(e) => setBeschreibung(e.target.value)}
                   placeholder="Optional" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Abbrechen
          </Button>
          <Button onClick={handleSave} disabled={isSaving} data-testid="schrank-building-save-btn">
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Speichern'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Floor Dialog
// =============================================================================
function FloorDialog({ open, onOpenChange, floor, onSave }) {
  const [name, setName] = useState('');
  const [etage, setEtage] = useState(0);
  const [width, setWidth] = useState(1200);
  const [height, setHeight] = useState(800);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(floor?.name || '');
      setEtage(floor?.etage ?? 0);
      setWidth(floor?.geometry?.width ?? 1200);
      setHeight(floor?.geometry?.height ?? 800);
    }
  }, [open, floor]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Name ist erforderlich');
      return;
    }
    setIsSaving(true);
    try {
      await onSave({
        name: name.trim(),
        etage: Number(etage) || 0,
        geometry: {
          width: Math.max(200, Number(width) || 1200),
          height: Math.max(200, Number(height) || 800),
        },
      });
      onOpenChange(false);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="schrank-dialog">
        <DialogHeader>
          <DialogTitle>{floor ? 'Etage bearbeiten' : 'Neue Etage'}</DialogTitle>
          <DialogDescription>
            Definiere Name, Etagennummer und Canvas-Größe für den Grundriss.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div>
            <Label htmlFor="f-name">Name *</Label>
            <Input id="f-name" value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="z.B. Erdgeschoss" data-testid="schrank-floor-name" />
          </div>
          <div>
            <Label htmlFor="f-etage">Etage (0=EG, 1=1.OG, -1=UG)</Label>
            <Input id="f-etage" type="number" value={etage}
                   onChange={(e) => setEtage(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="f-w">Canvas Breite (px)</Label>
              <Input id="f-w" type="number" value={width}
                     onChange={(e) => setWidth(e.target.value)} min={200} />
            </div>
            <div>
              <Label htmlFor="f-h">Canvas Höhe (px)</Label>
              <Input id="f-h" type="number" value={height}
                     onChange={(e) => setHeight(e.target.value)} min={200} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Abbrechen
          </Button>
          <Button onClick={handleSave} disabled={isSaving} data-testid="schrank-floor-save-btn">
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Speichern'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Main Page
// =============================================================================
export function SchrankRaumverwaltungPage() {
  const [permissions, setPermissions] = useState({ can_edit_grundriss: false, can_manage_zuweisung: false });
  const [buildings, setBuildings] = useState([]);
  const [floors, setFloors] = useState([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState(null);
  const [selectedFloorId, setSelectedFloorId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // View-Modus: 'overview' | 'floor' | 'room'
  const [view, setView] = useState('overview');
  const [activeRoom, setActiveRoom] = useState(null);

  // Dialogs
  const [buildingDialog, setBuildingDialog] = useState({ open: false, building: null });
  const [floorDialog, setFloorDialog] = useState({ open: false, floor: null });
  const [deleteBuilding, setDeleteBuilding] = useState(null);
  const [deleteFloor, setDeleteFloor] = useState(null);

  // Initial load
  useEffect(() => {
    const init = async () => {
      try {
        const [permRes, buildingsRes] = await Promise.all([
          schrankApi.getMyPermissions(),
          schrankApi.listBuildings(),
        ]);
        setPermissions(permRes.data);
        setBuildings(buildingsRes.data);
        if (buildingsRes.data.length > 0) {
          setSelectedBuildingId(buildingsRes.data[0].id);
        }
      } catch (e) {
        console.error(e);
        toast.error('Schrank-Daten konnten nicht geladen werden');
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, []);

  // Floors laden, wenn Building wechselt
  const loadFloors = useCallback(async (buildingId) => {
    if (!buildingId) {
      setFloors([]);
      setSelectedFloorId(null);
      return;
    }
    try {
      const res = await schrankApi.listFloors(buildingId);
      setFloors(res.data);
      setSelectedFloorId(res.data[0]?.id || null);
    } catch (e) {
      console.error(e);
      toast.error('Etagen konnten nicht geladen werden');
    }
  }, []);

  useEffect(() => {
    loadFloors(selectedBuildingId);
  }, [selectedBuildingId, loadFloors]);

  // CRUD Building
  const saveBuilding = async (data) => {
    if (buildingDialog.building) {
      const res = await schrankApi.updateBuilding(buildingDialog.building.id, data);
      setBuildings((prev) => prev.map(b => b.id === res.data.id ? res.data : b));
      toast.success('Gebäude aktualisiert');
    } else {
      const res = await schrankApi.createBuilding(data);
      setBuildings((prev) => [...prev, res.data]);
      setSelectedBuildingId(res.data.id);
      toast.success('Gebäude angelegt');
    }
  };

  const confirmDeleteBuilding = async () => {
    if (!deleteBuilding) return;
    try {
      await schrankApi.deleteBuilding(deleteBuilding.id);
      setBuildings((prev) => prev.filter(b => b.id !== deleteBuilding.id));
      if (selectedBuildingId === deleteBuilding.id) {
        setSelectedBuildingId(null);
        setFloors([]);
      }
      toast.success(`"${deleteBuilding.name}" gelöscht`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Löschen fehlgeschlagen');
    } finally {
      setDeleteBuilding(null);
    }
  };

  // CRUD Floor
  const saveFloor = async (data) => {
    if (floorDialog.floor) {
      const res = await schrankApi.updateFloor(floorDialog.floor.id, data);
      setFloors((prev) => prev.map(f => f.id === res.data.id ? res.data : f));
      toast.success('Etage aktualisiert');
    } else {
      const res = await schrankApi.createFloor(selectedBuildingId, data);
      setFloors((prev) => [...prev, res.data]);
      setSelectedFloorId(res.data.id);
      toast.success('Etage angelegt');
    }
  };

  const confirmDeleteFloor = async () => {
    if (!deleteFloor) return;
    try {
      await schrankApi.deleteFloor(deleteFloor.id);
      setFloors((prev) => prev.filter(f => f.id !== deleteFloor.id));
      if (selectedFloorId === deleteFloor.id) setSelectedFloorId(null);
      toast.success(`"${deleteFloor.name}" gelöscht`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Löschen fehlgeschlagen');
    } finally {
      setDeleteFloor(null);
    }
  };

  const selectedBuilding = buildings.find(b => b.id === selectedBuildingId);
  const selectedFloor = floors.find(f => f.id === selectedFloorId);
  const canEdit = permissions.can_edit_grundriss;

  if (isLoading) {
    return (
      <MainLayout>
        <div className="space-y-4">
          <Skeleton className="h-12 w-64" />
          <div className="grid lg:grid-cols-3 gap-4">
            <Skeleton className="h-64" />
            <Skeleton className="h-64" />
            <Skeleton className="h-64" />
          </div>
        </div>
      </MainLayout>
    );
  }

  // Editor-Modus: Etagen-Plan
  if (view === 'floor' && selectedFloor) {
    return (
      <MainLayout>
        <div className="schrank-workspace">
        <SchrankSectionHeader title="Raumplanung" context={[selectedBuilding?.name, selectedFloor?.name].filter(Boolean).join(" / ")} />
        <FloorPlanEditor
          floor={selectedFloor}
          canEdit={canEdit}
          onBack={() => {
            setView('overview');
            // Etagen nach möglichen Änderungen neu laden
            if (selectedBuildingId) loadFloors(selectedBuildingId);
          }}
          onOpenRoom={(room) => {
            setActiveRoom(room);
            setView('room');
          }}
        />
        </div>
      </MainLayout>
    );
  }

  // Editor-Modus: Raum-Detail (Schränke + Zuweisung)
  if (view === 'room' && activeRoom) {
    const activeBuilding = buildings.find(b => b.id === selectedBuildingId);
    const activeFloor = floors.find(f => f.id === selectedFloorId);
    return (
      <MainLayout>
        <div className="schrank-workspace">
        <SchrankSectionHeader title="Raumplanung" context={[activeBuilding?.name, activeFloor?.name, activeRoom?.name].filter(Boolean).join(" / ")} />
        <LockerWallEditor
          room={activeRoom}
          buildingName={activeBuilding?.name}
          floorName={activeFloor?.name}
          canEdit={canEdit}
          canAssign={permissions.can_manage_zuweisung}
          onBack={() => {
            setActiveRoom(null);
            setView('floor');
          }}
        />
        </div>
      </MainLayout>
    );
  }

  if (!permissions.can_edit_grundriss) {
    return (
      <MainLayout>
        <Card className="border-amber-300 bg-amber-50/60">
          <CardContent className="py-12 text-center space-y-3">
            <AlertTriangle className="w-10 h-10 text-amber-600 mx-auto" />
            <h2 className="font-semibold">Keine Berechtigung</h2>
            <p className="text-sm text-muted-foreground">
              Für die Raumplanung benötigst du die entsprechende Berechtigung.
            </p>
          </CardContent>
        </Card>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="schrank-workspace" data-testid="schrank-raumverwaltung-page">
        <SchrankSectionHeader title="Raumplanung" description="Gebäude, Etagen und Räume gestalten." />
        <div className="schrank-location-layout">
          <SchrankLocationNav buildings={buildings} floors={floors}
            buildingId={selectedBuildingId} floorId={selectedFloorId}
            onBuildingChange={setSelectedBuildingId} onFloorChange={setSelectedFloorId}
            management={canEdit ? {
              createBuilding: () => setBuildingDialog({ open: true, building: null }),
              editBuilding: building => setBuildingDialog({ open: true, building }),
              deleteBuilding: setDeleteBuilding,
              createFloor: () => setFloorDialog({ open: true, floor: null }),
              editFloor: floor => setFloorDialog({ open: true, floor }),
              deleteFloor: setDeleteFloor,
            } : undefined} />
          <SchrankFloorPreview floor={selectedFloor} onOpen={() => setView('floor')} />
        </div>

        {/* Dialogs */}
        <BuildingDialog
          open={buildingDialog.open}
          onOpenChange={(open) => setBuildingDialog((s) => ({ ...s, open }))}
          building={buildingDialog.building}
          onSave={saveBuilding}
        />
        <FloorDialog
          open={floorDialog.open}
          onOpenChange={(open) => setFloorDialog((s) => ({ ...s, open }))}
          floor={floorDialog.floor}
          onSave={saveFloor}
        />

        {/* Delete Confirmations */}
        <AlertDialog open={!!deleteBuilding} onOpenChange={(open) => !open && setDeleteBuilding(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-destructive" />
                Gebäude löschen?
              </AlertDialogTitle>
              <AlertDialogDescription>
                <strong>{deleteBuilding?.name}</strong> wird mit allen Etagen, Räumen und Schränken gelöscht.
                Diese Aktion kann nicht rückgängig gemacht werden.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDeleteBuilding}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Löschen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={!!deleteFloor} onOpenChange={(open) => !open && setDeleteFloor(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-destructive" />
                Etage löschen?
              </AlertDialogTitle>
              <AlertDialogDescription>
                <strong>{deleteFloor?.name}</strong> wird mit allen Räumen und Schränken gelöscht.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDeleteFloor}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Löschen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </MainLayout>
  );
}

export default SchrankRaumverwaltungPage;
